import { useCallback, useEffect, useState } from "react";
import { Button, Col, Divider, Modal, notification, Row } from "antd";
import SummaryTable from "./SummaryTable";
import { SweepPositionShow, UntrackedPositionShowSchema } from "../../../../../../models/Sweep";
import PositionsControls from "./PositionsControls";
import { useLanguage } from "../../../../../../context/LanguageContext";
import SweepService from "../../../../../../services/SweepService";
import { useParams } from "react-router-dom";

interface PositionsOutProps {
  sweepId: number;
  sweepStatus: string;
  showPictures: boolean;
}

const PositionsOutLayout = (props: PositionsOutProps) => {
  const { language } = useLanguage();
  const { sweepId } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [untrackedPositions, setUntrackedPositions] = useState<UntrackedPositionShowSchema[]>([]);
  const [positionFilter, setPositionFilter] = useState<string>("");
  const [palletFilter, setPalletFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string[]>([]);


  const onApplyFilters = useCallback(
    (
      position: string,
      pallet: string,
      status: string[]
    ) => {
      setPositionFilter(position);
      setPalletFilter(pallet);
      setStatusFilter(status);
    },
    []
  );

  const trimSpacesInPositionLabels = (untrackedPositions: UntrackedPositionShowSchema[]): UntrackedPositionShowSchema[] => {
    return untrackedPositions.map(position => {
      position.position_label = position.position_label.trim();
      return position;
    });
  };
  const updateContent = useCallback(() => {
    if (sweepId) {
      SweepService.getUntrakedPositions(sweepId).then((res) => {
        if (res === null) {
          return;
        }
        setUntrackedPositions(trimSpacesInPositionLabels(res))
        setLoading(false);
      });
    }
  }, []);

  useEffect(() => {
    updateContent();
    const interval = setInterval(() => {
      updateContent();
    }, 600000);
    return () => clearInterval(interval);
  }, [updateContent]);

  const process_positions = (untrackedPosition: UntrackedPositionShowSchema[]) => {
    var filtered = trimSpacesInPositionLabels(untrackedPosition)
    if (positionFilter !== "") {
      filtered = filtered.filter((entry) =>
        entry.position_label
          .toLocaleUpperCase()
          .includes(positionFilter.toLocaleUpperCase())
      );
    }
    if (palletFilter !== "") {
      filtered = filtered.filter((entry) => {
        if (entry.pallet_label === undefined || entry.pallet_label === null) {
          return "empty"
            .toLocaleUpperCase()
            .includes(palletFilter.toLocaleUpperCase());
        }
        return entry.pallet_label
          .toLocaleUpperCase()
          .includes(palletFilter.toLocaleUpperCase());
      });
    }
    if (positionFilter === "") {
      filtered = filtered.filter((entry) =>
        entry.position_label
          .toLocaleUpperCase()
          .slice(0, entry.position_label.length - 3)
      );
    }
    if (statusFilter.length > 0) {
      filtered = filtered
        .filter((entry) => statusFilter.includes(entry.tracking_status))
        .sort((a, b) => statusFilter.indexOf(a.tracking_status) - statusFilter.indexOf(b.tracking_status)); // Sorting by status logic
    }
    return filtered;
  }

  const handleAddAllConfirm = () => {
    Modal.confirm({
      title: language === "es" ? "¿Confirmar acción?" : "Confirm action?",
      content: language === "es"
        ? "¿Estás seguro de que querés agregar todas las posiciones?"
        : "Are you sure you want to add all positions?",
      okText: language === "es" ? "Sí, agregar" : "Yes, add",
      cancelText: language === "es" ? "Cancelar" : "Cancel",
      okType: "primary",
      onOk: addAll,
    });
  };

  const addAll = () => {
    SweepService.setAllUntrakedPositon(props.sweepId.toString(), "add").then((res) => {
      if (res === "OK") {
        notification.success({
          message: language === "es" ? "Éxito" : "Success",
          description: language === "es"
            ? "Posiciones agregadas correctamente."
            : "Positions add successfully.",
        });
        updateContent();
        setLoading(true);
      } else {
        notification.error({
          message: language === "es" ? "Error" : "Error",
          description: language === "es"
            ? "No se pudo agregar las posiciones."
            : "Failed to add all positions.",
        });
      }
    });
  };

  const handleDiscardAllConfirm = () => {
    Modal.confirm({
      title: language === "es" ? "¿Confirmar acción?" : "Confirm action?",
      content: language === "es"
        ? "¿Estás seguro de que querés descartar todas las posiciones?"
        : "Are you sure you want to discard all positions?",
      okText: language === "es" ? "Sí, descartar" : "Yes, discard",
      cancelText: language === "es" ? "Cancelar" : "Cancel",
      okType: "primary",
      onOk: discardAll,
    });
  };

  const discardAll = () => {
    SweepService.setAllUntrakedPositon(props.sweepId.toString(), "discard").then((res) => {
      if (res === "OK") {
        notification.success({
          message: language === "es" ? "Éxito" : "Success",
          description: language === "es"
            ? "Posiciones descartadas correctamente."
            : "Positions discard successfully.",
        });
        updateContent();
        setLoading(true);
      } else {
        notification.error({
          message: language === "es" ? "Error" : "Error",
          description: language === "es"
            ? "No se pudo descartar las posiciones."
            : "Failed to discard all position.",
        });
      }
    });
  };

  return (
    <>
      <Row
        gutter={[30, 16]}
        align="bottom"
        justify="center"
        style={{
          padding: "10px 20px",
        }}
      >
        <Col xs={24} sm={24} md={24} lg={15} >
          <PositionsControls onApply={onApplyFilters} />
        </Col>
        <Col xs={24} sm={12} md={12} lg={5} >
          <Button
            disabled={props.sweepStatus!="processing"||untrackedPositions.every((position) => (position.tracking_status === "accepted"))}
            type="primary"
            onClick={handleAddAllConfirm}
            style={{ backgroundColor: (props.sweepStatus!="processing"||untrackedPositions.every((position) => (position.tracking_status === "accepted"))) ? "" : "#73d13d", margin: "0 5px" }}
          >
            {language === "es" ? "Agregar todas" : "Add all"}
          </Button>
        </Col>
        <Col xs={24} sm={12} md={12}  lg={4} >
          <Button
            disabled={props.sweepStatus!="processing"||untrackedPositions.every((position) => (position.tracking_status === "rejected"))}
            style={{ backgroundColor: (props.sweepStatus!="processing"||untrackedPositions.every((position) => (position.tracking_status === "rejected"))) ? "" : "#ff4d4f", margin: "0 5px" }}
            type="primary"
            onClick={handleDiscardAllConfirm}
          >
            {language === "es" ? "Descartar todas" : "discard all"}
          </Button>
        </Col>
      </Row>
      <Divider />
      <SummaryTable
        positions={process_positions(untrackedPositions)}
        sweepId={props.sweepId ? props.sweepId : 0}
        showPictures={props.showPictures}
        updateContent={updateContent}
        loading={loading}
        sweepStatus={props.sweepStatus}
      />
    </>
  );
};

export default PositionsOutLayout;